import React from 'react';
import { createRoot } from 'react-dom/client';
import { registerDynamicBootstrap } from '../../shared/registerDynamicBootstrap';
import { getAccountFrontData } from '../frontData';
import { useDashboardAjaxNavigation } from './useDashboardAjaxNavigation';
import { useDashboardAvatar } from './useDashboardAvatar';
import { useDashboardGroupToggle } from './useDashboardGroupToggle';
import type { DashboardLoaderMode, DashboardMountConfig } from './types';
import './dashboard.css';

const runtimeMountId = 'ips-react-dashboard-runtime-root';

const asLoaderMode = (value: unknown): DashboardLoaderMode =>
  value === 'option-2' || value === 'option-3' ? value : 'option-1';

const asBool = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (value === '1' || value === 1 || value === 'true' || value === 'yes' || value === 'on') {
    return true;
  }

  if (value === '0' || value === 0 || value === 'false' || value === 'no' || value === 'off' || value === '') {
    return false;
  }

  return fallback;
};

const asString = (value: unknown, fallback: string): string => {
  if (typeof value === 'string' && value.trim() !== '') {
    return value.trim();
  }
  return fallback;
};

const readConfig = (): DashboardMountConfig => {
  const frontData = getAccountFrontData();
  const dashboardData = frontData.dashboard || {};
  const loaderData = dashboardData.loader || {};
  const avatarData = dashboardData.avatar || {};
  const selectorsData = dashboardData.selectors || {};

  return {
    enabled: asBool(dashboardData.enabled, true),
    ajaxNavigation: asBool(dashboardData.ajaxNavigation, false),
    ajaxNavigationScroll: asBool(dashboardData.ajaxNavigationScroll, true),
    contentSelector: asString(dashboardData.contentSelector, '#content, div.woocommerce'),
    loader: {
      mode: asLoaderMode(loaderData.mode),
      imageUrl: asString(loaderData.imageUrl, ''),
      progressColor: asString(loaderData.progressColor, '#0073aa'),
    },
    avatar: {
      enabled: asBool(avatarData.enabled, false),
      ajaxUrl: asString(avatarData.ajaxUrl, ''),
      actionNonce: asString(avatarData.actionNonce, ''),
    },
    selectors: {
      navigationLinks: asString(
        selectorsData.navigationLinks,
        'li.has-ajax-navigation a, table.woocommerce-orders-table a.view, .woocommerce-Addresses a.edit'
      ),
      groupOpener: asString(selectorsData.groupOpener, '.group-opener'),
      avatarTrigger: asString(selectorsData.avatarTrigger, '.ips-wcmap .user-avatar'),
    },
  };
};

const bootstrap = () => {
  const config = readConfig();
  if (!config.enabled) {
    return;
  }

  let mount = document.getElementById(runtimeMountId) as HTMLElement | null;
  if (!mount) {
    mount = document.createElement('div');
    mount.id = runtimeMountId;
    mount.style.display = 'none';
    document.body.appendChild(mount);
  }

  if (mount.dataset.ipsReactMounted === '1') {
    return;
  }

  mount.dataset.ipsReactMounted = '1';

  const root = createRoot(mount);
  const App: React.FC = () => {
    useDashboardAjaxNavigation(config);
    useDashboardGroupToggle(config.selectors.groupOpener);
    useDashboardAvatar(config.avatar, config.selectors.avatarTrigger);
    return null;
  };

  root.render(<App />);
};

registerDynamicBootstrap(bootstrap);
